/**
 * אחסון מקומי קטן לרשומות עם id — IndexedDB כשאפשר, זיכרון כשלא (גלישה פרטית, חסימה).
 * ⚠️ כשל באחסון לעולם לא זורק החוצה: מי שמשתמש בזה (תור הצילומים) חייב להמשיך לעבוד
 * גם בלי התמדה — רק בלי לשרוד סגירת אפליקציה.
 */

export interface RecordStore<T extends { id: string }> {
  all(): Promise<T[]>;
  put(v: T): Promise<void>;
  del(id: string): Promise<void>;
}

export function memoryStore<T extends { id: string }>(): RecordStore<T> {
  const m = new Map<string, T>();
  return {
    all: async () => [...m.values()],
    put: async (v) => { m.set(v.id, v); },
    del: async (id) => { m.delete(id); },
  };
}

function req<R>(r: IDBRequest<R>): Promise<R> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export function idbStore<T extends { id: string }>(dbName: string, storeName: string): RecordStore<T> {
  const fallback = memoryStore<T>();
  let dbp: Promise<IDBDatabase | null> | null = null;

  const open = (): Promise<IDBDatabase | null> => {
    if (!dbp) {
      dbp = new Promise<IDBDatabase | null>((resolve) => {
        try {
          if (typeof indexedDB === 'undefined') { resolve(null); return; }
          const r = indexedDB.open(dbName, 1);
          r.onupgradeneeded = () => {
            if (!r.result.objectStoreNames.contains(storeName)) r.result.createObjectStore(storeName, { keyPath: 'id' });
          };
          r.onsuccess = () => resolve(r.result);
          r.onerror = () => resolve(null);
          r.onblocked = () => resolve(null);
        } catch {
          resolve(null);
        }
      });
    }
    return dbp;
  };

  // כל פעולה: IndexedDB, ואם הוא לא זמין או נכשל — הזיכרון (כתיבה נכשלת לא מאבדת את הרשומה לסשן הזה)
  const withStore = async <R>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<R>, fb: () => Promise<R>): Promise<R> => {
    const db = await open();
    if (!db) return fb();
    try {
      return await req(fn(db.transaction(storeName, mode).objectStore(storeName)));
    } catch {
      return fb();
    }
  };

  return {
    all: async () => {
      const fromDb = await withStore('readonly', (s) => s.getAll() as IDBRequest<T[]>, () => fallback.all());
      // רשומות שנכתבו רק לזיכרון (כתיבה ל-IndexedDB שנכשלה) — שלא ייעלמו מהרשימה
      const mem = await fallback.all();
      const ids = new Set(fromDb.map((r) => r.id));
      return [...fromDb, ...mem.filter((r) => !ids.has(r.id))];
    },
    put: async (v) => {
      await withStore('readwrite', (s) => s.put(v), async () => { await fallback.put(v); return v.id; });
    },
    del: async (id) => {
      await fallback.del(id);
      await withStore('readwrite', (s) => s.delete(id), async () => undefined);
    },
  };
}
